/**
 * Analytics Performance Monitor
 * Tracks and reports analytics impact on UI performance
 */

interface PerformanceMetrics {
    eventName: string;
    duration: number;
    blocked: boolean;
    timestamp: number;
}

const performanceMetrics: PerformanceMetrics[] = [];
const MAX_METRICS = 100;

/**
 * Wrap analytics calls with performance monitoring
 */
export function measureAnalyticsPerformance<T extends (...args: any[]) => any>(
    fn: T,
    eventName: string
): T {
    return ((...args: Parameters<T>) => {
        const startTime = performance.now();
        const isMainThread = !window.requestIdleCallback;

        try {
            const result = fn(...args);

            // Handle both sync and async functions
            if (result instanceof Promise) {
                result.finally(() => {
                    recordMetric(eventName, startTime, false);
                });
            } else {
                recordMetric(eventName, startTime, false);
            }

            return result;
        } catch (error) {
            recordMetric(eventName, startTime, true);
            throw error;
        }
    }) as T;
}

/**
 * Record performance metric
 */
function recordMetric(eventName: string, startTime: number, blocked: boolean) {
    const duration = performance.now() - startTime;

    // Only record if it took more than 1ms
    if (duration > 1) {
        performanceMetrics.push({
            eventName,
            duration,
            blocked,
            timestamp: Date.now(),
        });

        // Keep only recent metrics
        if (performanceMetrics.length > MAX_METRICS) {
            performanceMetrics.shift();
        }

        // Warn if analytics is taking too long
        if (duration > 50) {
            console.warn(
                `[Analytics Performance] Event "${eventName}" took ${duration.toFixed(
                    2
                )}ms - consider optimization`
            );
        }
    }
}

/**
 * Get performance report
 */
export function getAnalyticsPerformanceReport() {
    const totalEvents = performanceMetrics.length;
    if (totalEvents === 0) {
        return {
            totalEvents: 0,
            averageDuration: 0,
            maxDuration: 0,
            blockedEvents: 0,
            performanceImpact: 'none',
        };
    }

    const totalDuration = performanceMetrics.reduce(
        (sum, m) => sum + m.duration,
        0
    );
    const averageDuration = totalDuration / totalEvents;
    const maxDuration = Math.max(...performanceMetrics.map((m) => m.duration));
    const blockedEvents = performanceMetrics.filter((m) => m.blocked).length;

    // Determine performance impact level
    let performanceImpact: 'none' | 'low' | 'medium' | 'high';
    if (averageDuration < 5) {
        performanceImpact = 'none';
    } else if (averageDuration < 20) {
        performanceImpact = 'low';
    } else if (averageDuration < 50) {
        performanceImpact = 'medium';
    } else {
        performanceImpact = 'high';
    }

    return {
        totalEvents,
        averageDuration: Number(averageDuration.toFixed(2)),
        maxDuration: Number(maxDuration.toFixed(2)),
        blockedEvents,
        performanceImpact,
        recentEvents: performanceMetrics.slice(-10).map((m) => ({
            event: m.eventName,
            duration: Number(m.duration.toFixed(2)),
            blocked: m.blocked,
        })),
    };
}

/**
 * Monitor for long-running analytics operations
 */
export function enablePerformanceMonitoring() {
    // Check performance every 30 seconds
    setInterval(() => {
        const report = getAnalyticsPerformanceReport();

        if (report.performanceImpact === 'high') {
            console.error(
                '[Analytics Performance] High impact detected:',
                report
            );
        } else if (report.performanceImpact === 'medium') {
            console.warn(
                '[Analytics Performance] Medium impact detected:',
                report
            );
        }
    }, 30000);

    // Expose performance report globally for debugging
    if (typeof window !== 'undefined') {
        (window as any).__analyticsPerformance = getAnalyticsPerformanceReport;
    }
}

/**
 * Debounce analytics calls to prevent flooding
 */
export function debounceAnalytics<T extends (...args: any[]) => any>(
    fn: T,
    delay: number = 100
): T {
    let timeoutId: NodeJS.Timeout | null = null;
    let lastArgs: Parameters<T> | null = null;

    return ((...args: Parameters<T>) => {
        lastArgs = args;

        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            if (lastArgs) {
                fn(...lastArgs);
                lastArgs = null;
            }
        }, delay);
    }) as T;
}

/**
 * Throttle analytics calls to limit frequency
 */
export function throttleAnalytics<T extends (...args: any[]) => any>(
    fn: T,
    limit: number = 1000
): T {
    let lastCall = 0;

    return ((...args: Parameters<T>) => {
        const now = Date.now();

        if (now - lastCall >= limit) {
            lastCall = now;
            return fn(...args);
        }
    }) as T;
}
